import { isImmutable, List, Map } from 'immutable';

import { shapeFoldersForApp, shapeImagesForApp } from '../utils/studio/shapeForApp';
import { shapeFileForEditor } from '../utils/photoEditor/state';
import { createController } from '../utils/controller';
import { downloadFile } from '../utils/files';
import { getFilters } from '../utils/studio/filters';
import { increase } from '../utils/math';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { IMAGES_PER_PAGE, ORDER_BY } from '../constants/studio';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { EMPTY, SEPARATOR } from '../constants';
import { ETSY, SHOPIFY } from '../constants/channels';
import { FEATURE } from '../constants/features';

import actions from '../actions';

const imagesController = createController();
const foldersController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    imagesController,
    foldersController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn(['mediaLibrary', 'channel']);
  const db = state.getIn(['mediaLibrary', 'db']);
  const shopId = state.getIn(['mediaLibrary', 'shopId']);
  return { channel, db, shopId };
}

function* bootstrap(reduction, { channel, db, shopId }) {
  abortAll();

  yield function effect(dispatch) {
    dispatch(actions.mediaLibrary.getFolders());
    dispatch(actions.mediaLibrary.getImages());
  };

  let state = Map({ channel, db, shopId });

  if (reduction.hasIn(['mediaLibrary', 'uploading'])) {
    state = state.set('uploading', reduction.getIn(['mediaLibrary', 'uploading']));
  }

  return reduction.set('mediaLibrary', state);
}

function* cleanUp(reduction) {
  abortAll();

  let state = EMPTY.MAP;

  if (reduction.hasIn(['mediaLibrary', 'uploading'])) {
    state = state.set('uploading', reduction.getIn(['mediaLibrary', 'uploading']));
  }

  return reduction.set('mediaLibrary', state);
}

function* createFolder(reduction, { callback, name }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.createFolder({ db, name, shopId })
      .then(
        ({ uuid }) => dispatch(actions.mediaLibrary.createFolder.fulfilled({ callback, name, uuid })),
        (error) => dispatch(actions.mediaLibrary.createFolder.rejected(error)),
      );
  };

  return reduction;
}

function* createFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_FOLDER,
      })
    );
  };

  return reduction;
}

function* createFolderFulfilled(reduction, { callback, name, uuid }) {
  yield function effect(dispatch) {
    if (typeof callback === 'function') {
      callback(uuid);
    } else {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.SUCCESS,
          message: MESSAGE.SUCCESS.CREATE_FOLDER,
        })
      );
    }

    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.mediaLibrary.getFolders());
    dispatch(actions.mediaLibrary.getImages({ folder: uuid }));
  };

  function updateById(byId) {
    return byId.set(uuid, Map({ count: 0, name, uuid }));
  }

  function updateIds(ids) {
    return ids.push(uuid);
  }

  return reduction
    .updateIn(['mediaLibrary', 'folders', 'byId'], updateById)
    .updateIn(['mediaLibrary', 'folders', 'ids'], updateIds);
}

function* deleteFolder(reduction, folder) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.deleteFolder({ db, folder, shopId })
      .then(
        () => dispatch(actions.mediaLibrary.deleteFolder.fulfilled(folder)),
        (error) => dispatch(actions.mediaLibrary.deleteFolder.rejected(error)),
      );
  };

  return reduction;
}

function* deleteFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_FOLDER,
      })
    );
  };

  return reduction;
}

function* deleteFolderFulfilled(reduction, uuid) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.mediaLibrary.getFolders());
    dispatch(actions.mediaLibrary.getImages({ folder: null }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_FOLDER,
      })
    );
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(item) {
      return item !== uuid;
    });
  }

  return reduction
    .deleteIn(['mediaLibrary', 'folders', 'byId', uuid])
    .updateIn(['mediaLibrary', 'folders', 'ids'], updateIds);
}

function* deleteImages(reduction, { uuids }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(
      actions.mediaLibrary.toggleImageState({
        images: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.deleteImages({ db, shopId, uuids })
      .then(
        () => dispatch(actions.mediaLibrary.deleteImages.fulfilled(shopId)),
        (error) => dispatch(actions.mediaLibrary.deleteImages.rejected({ error, shopId })),
      );
  };

  return reduction;
}

function* deleteImagesRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DELETE_PHOTOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(actions.data.setData({ path: 'lastInteraction' }));
      dispatch(actions.mediaLibrary.getFolders());
      dispatch(actions.mediaLibrary.getImages({ clear: true }));
    }
  };

  return reduction;
}

function* deleteImagesFulfilled(reduction, shopId) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.SUCCESS.DELETE_PHOTOS,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(actions.data.setData({ path: 'lastInteraction' }));
      dispatch(actions.mediaLibrary.getFolders());
      dispatch(actions.mediaLibrary.getImages({ clear: true }));
    }
  };

  return reduction;
}

function* downloadImages(reduction, { name, uuids }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(
      actions.mediaLibrary.toggleImageState({
        images: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.downloadImages({ db, shopId, uuids })
      .then((blob) => new Promise(function executor(resolve, reject) {
        const reader = new FileReader();

        reader.addEventListener('loadend', function onReaderLoad() {
          try {
            downloadFile({ data: reader.result, name });
            resolve();
          } catch (error) {
            reject(error);
          }
        });

        reader.readAsDataURL(blob);
      }))
      .then(
        () => dispatch(actions.mediaLibrary.downloadImages.fulfilled({ shopId, uuids })),
        (error) => dispatch(actions.mediaLibrary.downloadImages.rejected({ error, shopId, uuids })),
      );
  };

  return reduction;
}

function* downloadImagesRejected(reduction, { error, shopId, uuids }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DOWNLOAD_PHOTOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(
        actions.mediaLibrary.toggleImageState({
          images: uuids,
          state: 'processing',
          value: false,
        })
      );
    }
  };

  return reduction;
}

function* downloadImagesFulfilled(reduction, { shopId, uuids }) {
  if (shopId === getShopParams(reduction).shopId) {
    yield function effect(dispatch) {
      dispatch(
        actions.mediaLibrary.toggleImageState({
          images: uuids,
          state: 'processing',
          value: false,
        })
      );
    };
  }

  return reduction;
}

function* getFolders(reduction) {
  const { db, shopId } = getShopParams(reduction);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    api.studio.getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.mediaLibrary.getFolders.fulfilled(response)),
        (error) => dispatch(actions.mediaLibrary.getFolders.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getFoldersRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction
    .setIn(['mediaLibrary', 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn(['mediaLibrary', 'loaded'], reduction.hasIn(['mediaLibrary', 'images']));
}

function* getFoldersFulfilled(reduction, response) {
  abort(foldersController);

  let state = reduction.get('mediaLibrary');
  const allPhotos = Map({ count: parseInt(response.image_count) || 0, name: 'All photos' });
  const folders = response.folders.reduce(
    shapeFoldersForApp,
    state
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allPhotos))
      .set('ids', List([null]))
  );

  state = state
    .set('folders', folders)
    .set('loaded', state.has('images'));

  if (state.get('loaded') && !state.getIn(['request', 'folder'])) {
    state = state.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return reduction.set('mediaLibrary', state);
}

function* getImages(reduction, payload = {}) {
  const { signal } = imagesController.start();
  const { db, shopId } = getShopParams(reduction);
  let state = reduction.get('mediaLibrary').setIn(['request', 'processing'], true);
  const studioV2Enabled = reduction.getIn(['user', 'features', FEATURE.STUDIO_V2]);
  const {
    folder = state.getIn(['request', 'folder', 'uuid'], null),
    order = { by: state.getIn(['order', 'by'], null), type: state.getIn(['order', 'type'], false) },
  } = payload;

  let {
    clear = false,
    page = state.getIn(['request', 'page'], 0),
    query = state.getIn(['request', 'query'], ''),
  } = payload;

  if (state.has('folders') && folder !== state.getIn(['folders', 'selected'])) {
    state = state.setIn(['folders', 'selected'], folder);
  }

  if (folder !== state.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== state.getIn(['request', 'query'], '')) {
    clear = true;
    state = state.set('query', query);
  } else if (order.by !== state.getIn(['order', 'by'], null) || order.type !== state.getIn(['order', 'type'], false)) {
    clear = true;

    if (order.by) {
      state = state
        .setIn(['order', 'by'], order.by)
        .setIn(['order', 'type'], order.type);
    } else {
      order.by = undefined;
      order.type = undefined;
      state = state
        .deleteIn(['order', 'by'])
        .deleteIn(['order', 'type']);
    }
  }

  if (!order.by && studioV2Enabled) {
    order.by = ORDER_BY.LISTINGS_CONNECTED;
    order.type = false;
    state = state
      .setIn(['order', 'by'], order.by)
      .setIn(['order', 'type'], order.type);
  }

  if (clear) {
    state = state.setIn(['images', 'loading'], true);
    page = 0;
  }

  const request = Map({
    folder: state.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    api.studio.getImages({ db, filters: getFilters({ order, page, query }), folder, shopId, signal })
      .then(
        (response) => dispatch(actions.mediaLibrary.getImages.fulfilled({ clear, request, response })),
        (error) => dispatch(actions.mediaLibrary.getImages.rejected({ error, request, signal })),
      );
  };

  return reduction.set('mediaLibrary', state);
}

function* getImagesRejected(reduction, { error, request, signal }) {
  if (signal.aborted) return reduction;

  abort(imagesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const state = reduction
    .get('mediaLibrary')
    .set('images', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return reduction.set('mediaLibrary', state.set('loaded', state.has('folders')));
}

function* getImagesFulfilled(reduction, { clear, request, response }) {
  abort(imagesController);

  let state = reduction.get('mediaLibrary');
  let images = state
    .get('images', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    images = images
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  state = state
    .set('images', response.images.reduce(shapeImagesForApp, images))
    .set('loaded', state.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < images.get('total')) {
    state = state.setIn(['request', 'hasMore'], true);
  }

  if (state.get('loaded') && !request.get('folder') && getSize(state.getIn(['folders', 'byId']))) {
    state = state.setIn(['request', 'folder'],
      state.getIn(['folders', 'byId', state.getIn(['folders', 'selected'], null)])
    );
  }

  return reduction.set('mediaLibrary', state);
}

function* moveImages(reduction, { source, target, uuids }) {
  if (source === target || !Array.isArray(uuids) || !getSize(uuids)) return reduction;

  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.moveImages({ db, shopId, source, target, uuids })
      .then(
        () => dispatch(actions.mediaLibrary.moveImages.fulfilled({ target, uuids })),
        (error) => dispatch(actions.mediaLibrary.moveImages.rejected(error)),
      );
  };

  return reduction;
}

function* moveImagesRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.MOVE_PHOTOS,
      })
    );
  };

  return reduction;
}

function* moveImagesFulfilled(reduction, { target, uuids }) {
  const { shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.mediaLibrary.getFolders());

    if (reduction.getIn(['mediaLibrary', 'folders', 'selected']) || !target) {
      dispatch(actions.mediaLibrary.getImages({ clear: true }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.PHOTOS_MOVED,
        folder: Map({
          title: reduction.getIn(['mediaLibrary', 'folders', 'byId', target, 'name']),
          uuid: target,
        }),
        shopId,
      })
    );
  };

  function reduceSelectedImages(result, uuid) {
    return result.remove(uuid);
  }

  const path = ['mediaLibrary', 'images', 'selected'];
  const selectedImages = uuids.reduce(reduceSelectedImages, reduction.getIn(path, EMPTY.SET));

  return reduction.setIn(path, selectedImages);
}

function* renameFolder(reduction, { folder, name }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.renameFolder({ db, folder, name, shopId })
      .then(
        () => dispatch(actions.mediaLibrary.renameFolder.fulfilled({ name, uuid: folder })),
        (error) => dispatch(actions.mediaLibrary.renameFolder.rejected(error)),
      );
  };

  return reduction;
}

function* renameFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.RENAME_FOLDER,
      })
    );
  };

  return reduction;
}

function* renameFolderFulfilled(reduction, { name, uuid }) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.mediaLibrary.getFolders());
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.RENAME_FOLDER,
      })
    );
  };

  function updateFolder(folder) {
    return folder.set('name', name);
  }

  return reduction.updateIn(['mediaLibrary', 'folders', 'byId', uuid], updateFolder);
}

function* saveImage(state, { channel, db, folder, photo, shopId }) {
  yield function effect(dispatch) {
    const mediaLibrary = state.get('mediaLibrary');
    const uuid = photo.get('uuid');

    if (!mediaLibrary.hasIn(['images', 'processing', uuid])) {
      dispatch(
        actions.mediaLibrary.toggleImageState({
          images: uuid,
          state: 'processing',
          value: true,
        })
      );
    }

    if (!mediaLibrary.hasIn(['images', 'byId', uuid])) {
      dispatch(
        actions.mediaLibrary.setValue({
          path: ['images', 'byId', uuid],
          value: photo,
        })
      );

      dispatch(
        actions.mediaLibrary.setValue({
          path: ['images', 'ids'],
          value: mediaLibrary.getIn(['images', 'ids'], EMPTY.LIST).unshift(uuid),
        })
      );
    }

    function onFail(error) {
      dispatch(actions.mediaLibrary.saveImage.rejected({ error, folder, shopId }));
    }

    function updatePhoto(updated) {
      dispatch(actions.mediaLibrary.saveImage({ channel, db, folder, photo: updated, shopId }));
    }

    function onUploaded({ url }) {
      updatePhoto(photo.set('url', url));
    }

    const url = photo.get('url');

    if (!url) {
      const data = photo.get('data');
      const mime = photo.get('mime');

      api.studio.uploadToS3({ data, db, mime, shopId })
        .then(onUploaded)
        .catch(onFail);

      return;
    }

    function onGenerated({ altText }) {
      updatePhoto(photo.set('altText', altText.trim().replace(/\s+/g, SEPARATOR.SPACE)));
    }

    const altText = photo.get('altText');
    const fileName = photo.get('fileName');
    const format = photo.get('format');

    switch (channel) {
      case ETSY:
      case SHOPIFY: {
        if (!photo.get('altText')) {
          const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

          api.ai.generateAltText({ image: url, v2, channel, db, shopId })
            .then(onGenerated)
            .catch(onFail);

          return;
        }

        break;
      }

      default: {
        break;
      }
    }

    function onSuccess() {
      dispatch(actions.mediaLibrary.saveImage.fulfilled({ folder, shopId }));
    }

    api.studio
      .savePhoto({ altText, db, folder, format, name: fileName, shopId, url })
      .then(onSuccess)
      .catch(onFail);
  };

  return state;
}

function* saveImageFulfilled(state, { folder, shopId }) {
  yield function effect(dispatch) {
    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.mediaLibrary.getFolders());

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.mediaLibrary.getImages({ clear: true }));
      }
    }
  };

  return state;
}

function* saveImageRejected(state, { error, folder, shopId }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.SAVE_PHOTO,
        type: NOTIFICATION.ERROR,
      })
    );

    if (error) {
      console.error(error);
    }

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.mediaLibrary.getFolders());

      if (
        !state.getIn(['mediaLibrary', 'request', 'processing']) &&
        folder === state.getIn(['mediaLibrary', 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.mediaLibrary.getImages({ clear: true }));
      }
    }
  };

  return state;
}

function* setValue(reduction, { path, value }) {
  if (!path) return reduction;

  const fullPath = Array.isArray(path)
    ? ['mediaLibrary', ...path]
    : ['mediaLibrary', path];

  return value === undefined
    ? reduction.deleteIn(fullPath)
    : reduction.setIn(fullPath, value);
}

function* toggleImageState(reduction, { images, state: path, value }) {
  function reduceImageIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = ['mediaLibrary', 'images', path];
  const state = typeof images === 'string'
    ? reduceImageIds(reduction.getIn(fullPath, EMPTY.SET), images)
    : Array.isArray(images) || isImmutable(images)
      ? images.reduce(reduceImageIds, reduction.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(state)
    ? reduction.setIn(fullPath, state)
    : reduction.deleteIn(fullPath);
}

function* updateImage(state, { channel, db, folder, photo, shopId }) {
  yield function effect(dispatch) {
    const mediaLibrary = state.get('mediaLibrary');
    const uuid = photo.get('uuid');

    if (!mediaLibrary.hasIn(['images', 'processing', uuid])) {
      dispatch(
        actions.mediaLibrary.toggleImageState({
          images: uuid,
          state: 'processing',
          value: true,
        })
      );
    }

    function onFail(error) {
      dispatch(actions.mediaLibrary.updateImage.rejected({ error, folder, shopId }));
    }

    function updatePhoto(updated) {
      dispatch(actions.mediaLibrary.updateImage({ channel, db, folder, photo: updated, shopId }));
    }

    function onUploaded({ url }) {
      updatePhoto(photo.set('url', url));
    }

    const url = photo.get('url');

    if (!url) {
      const data = photo.get('data');
      const mime = photo.get('mime');

      api.studio.uploadToS3({ data, db, mime, shopId })
        .then(onUploaded)
        .catch(onFail);

      return;
    }

    function onGenerated({ altText }) {
      updatePhoto(photo.set('altText', altText.trim().replace(/\s+/g, SEPARATOR.SPACE)));
    }

    const altText = photo.get('altText');
    const fileName = photo.get('fileName');
    const format = photo.get('format');

    switch (channel) {
      case ETSY:
      case SHOPIFY: {
        if (!photo.get('altText')) {
          const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

          api.ai.generateAltText({ image: url, v2, channel, db, shopId })
            .then(onGenerated)
            .catch(onFail);

          return;
        }

        break;
      }

      default: {
        break;
      }
    }

    function onSuccess() {
      dispatch(actions.mediaLibrary.updateImage.fulfilled({ folder, shopId }));
    }

    dispatch(
      actions.mediaLibrary.setValue({
        path: ['images', 'byId', uuid],
        value: photo,
      })
    );

    const isAltTextOnly =
      altText !== undefined &&
      fileName === undefined &&
      format === undefined;

    const request = isAltTextOnly
      ? api.studio.updatePhotoAltText({ altText, db, shopId, uuid })
      : api.studio.updatePhoto({ altText, db, folder, format, name: fileName, shopId, url, uuid });

    request.then(onSuccess).catch(onFail);
  };

  return state;
}

function* updateImageFulfilled(state, { folder, shopId }) {
  yield function effect(dispatch) {
    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.mediaLibrary.getFolders());

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.mediaLibrary.getImages({ clear: true }));
      }
    }
  };

  return state;
}

function* updateImageRejected(state, { error, folder, shopId }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.SAVE_PHOTO,
        type: NOTIFICATION.ERROR,
      })
    );

    if (error) {
      console.error(error);
    }

    if (shopId === getShopParams(state).shopId) {
      dispatch(actions.mediaLibrary.getFolders());

      if (
        !state.getIn(['mediaLibrary', 'request', 'processing']) &&
        folder === state.getIn(['mediaLibrary', 'request', 'folder', 'uuid'])
      ) {
        dispatch(actions.data.setData({ path: 'lastInteraction' }));
        dispatch(actions.mediaLibrary.getImages({ clear: true }));
      }
    }
  };

  return state;
}

function* uploadFiles(reduction, { files }) {
  const current = getShopParams(reduction);
  let state = reduction.get('mediaLibrary');

  if (getSize(files) === 0) {
    const folder = state.getIn(['uploading', 'folder']);
    const count = state.getIn(['uploading', 'count']);
    const shopId = state.getIn(['uploading', 'shopId']);
    const total = state.getIn(['uploading', 'total']);

    if (total) {
      yield function effect(dispatch) {
        if (!count) {
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.UPLOAD_FILES,
              type: NOTIFICATION.ERROR,
            })
          );

          return;
        }

        dispatch(
          actions.notifications.add({
            message: count === total
              ? MESSAGE.SUCCESS.UPLOAD_FILES
              : `${count} of ${total} files uploaded`,
            type: NOTIFICATION.SUCCESS,
          })
        );

        if (shopId === current.shopId) {
          dispatch(actions.mediaLibrary.getFolders());

          if (
            !state.getIn(['request', 'processing']) &&
            folder === state.getIn(['request', 'folder', 'uuid'])
          ) {
            dispatch(actions.data.setData({ path: 'lastInteraction' }));
            dispatch(actions.mediaLibrary.getImages({ clear: true }));
          }
        }
      };
    }

    return reduction.set('mediaLibrary', state.delete('uploading'));
  }

  if (!state.get('uploading')) {
    const { channel, db, shopId } = current;
    const folder = state.getIn(['request', 'folder', 'uuid']);
    const total = getSize(files);
    const uploading = Map({
      channel,
      count: 0,
      db,
      folder,
      images: Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }),
      shopId,
      total,
    });

    state = state.setIn(['uploading'], uploading);
  }

  yield function effect(dispatch) {
    const file = files.first();
    const channel = state.getIn(['uploading', 'channel']);
    shapeFileForEditor({ channel, file })
      .then(
        (photo) => dispatch(actions.mediaLibrary.uploadFiles.fulfilled({ files, photo })),
        (error) => dispatch(actions.mediaLibrary.uploadFiles.rejected({ error, files })),
      );
  };

  return reduction.set('mediaLibrary', state);
}

function* uploadFilesRejected(reduction, { error, files, photo }) {
  let state = reduction.get('mediaLibrary');

  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.mediaLibrary.uploadFiles({ files: files.shift() }));
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(id) {
      return id !== photo.id;
    });
  }

  if (photo) {
    state = state
      .deleteIn(['uploading', 'images', 'byId', photo.id])
      .updateIn(['uploading', 'images', 'ids'], updateIds);
  }

  return reduction.set('mediaLibrary', state);
}

function* uploadFilesFulfilled(reduction, { files, photo, uploaded }) {
  const current = getShopParams(reduction);
  let state = reduction.get('mediaLibrary');

  if (uploaded) {
    const folder = state.getIn(['uploading', 'folder']);
    const shopId = state.getIn(['uploading', 'shopId']);

    if (shopId === current.shopId) {
      if (folder) {
        state = state
          .updateIn(['folders', 'byId', null, 'count'], increase)
          .updateIn(['folders', 'byId', folder, 'count'], increase);
      } else {
        state = state.updateIn(['folders', 'byId', null, 'count'], increase);
      }

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        const image = state
          .getIn(['uploading', 'images', 'byId', photo.id])
          .set('alt_text', uploaded.altText)
          .set('url', uploaded.url)
          .set('uuid', uploaded.uuid);

        state = state
          .updateIn(['images', 'total'], increase)
          .setIn(['images', 'byId', uploaded.uuid], image)
          .updateIn(['images', 'ids'], function updateIds(ids) {
            return ids.unshift(uploaded.uuid);
          });
      }
    }

    state = state
      .updateIn(['uploading', 'count'], increase)
      .deleteIn(['uploading', 'images', 'byId', photo.id])
      .updateIn(['uploading', 'images', 'ids'], function updateIds(ids) {
        return ids.filter(function filterIds(id) {
          return id !== photo.id;
        });
      });

    yield function effect(dispatch) {
      dispatch(actions.mediaLibrary.uploadFiles({ files: files.shift() }));
    };
  } else {
    const { data, format, id, mime, name, url: preview } = photo;
    state = state
      .updateIn(['uploading', 'images', 'byId'], function updateById(byId = EMPTY.MAP) {
        return byId.set(id,
          Map({
            fileName: name,
            format,
            mime,
            url: preview,
            uuid: id,
          })
        );
      })
      .updateIn(['uploading', 'images', 'ids'], function update(ids = EMPTY.LIST) {
        return ids.push(id);
      });

    yield function effect(dispatch) {
      const channel = state.getIn(['uploading', 'channel']);
      const db = state.getIn(['uploading', 'db']);
      const folder = state.getIn(['uploading', 'folder']);
      const shopId = state.getIn(['uploading', 'shopId']);

      function onFail(error) {
        dispatch(actions.mediaLibrary.uploadFiles.rejected({ error, files, photo }));
      }

      function onUploaded({ url }) {
        function savePhoto({ altText = '' } = {}) {
          function onSaved({ fileUuid: uuid }) {
            dispatch(
              actions.mediaLibrary.uploadFiles.fulfilled({
                files,
                photo,
                uploaded: {
                  altText,
                  url,
                  uuid,
                },
              })
            );
          }

          api.studio.savePhoto({ altText, db, folder, format, name, shopId, url })
            .then(onSaved)
            .catch(onFail);
        }

        function onGenerated({ altText }) {
          savePhoto({ altText: altText.trim().replace(/\s+/g, SEPARATOR.SPACE) });
        }

        switch (channel) {
          case ETSY:
          case SHOPIFY: {
            if (!photo.alt_text) {
              const v2 = state.getIn(['user', 'features', FEATURE.AI_V2]);

              api.ai.generateAltText({ image: url, v2, channel, db, shopId })
                .then(onGenerated)
                .catch(onFail);
            } else {
              savePhoto();
            }

            break;
          }

          default: {
            savePhoto();
            break;
          }
        }
      }

      api.studio.uploadToS3({ data, db, mime, shopId })
        .then(onUploaded)
        .catch(onFail);
    };
  }

  return reduction.set('mediaLibrary', state);
}

const mediaLibrary = {
  [actions.mediaLibrary.bootstrap]: bootstrap,
  [actions.mediaLibrary.cleanUp]: cleanUp,
  [actions.mediaLibrary.createFolder]: createFolder,
  [actions.mediaLibrary.createFolder.fulfilled]: createFolderFulfilled,
  [actions.mediaLibrary.createFolder.rejected]: createFolderRejected,
  [actions.mediaLibrary.deleteFolder]: deleteFolder,
  [actions.mediaLibrary.deleteFolder.fulfilled]: deleteFolderFulfilled,
  [actions.mediaLibrary.deleteFolder.rejected]: deleteFolderRejected,
  [actions.mediaLibrary.deleteImages]: deleteImages,
  [actions.mediaLibrary.deleteImages.fulfilled]: deleteImagesFulfilled,
  [actions.mediaLibrary.deleteImages.rejected]: deleteImagesRejected,
  [actions.mediaLibrary.downloadImages]: downloadImages,
  [actions.mediaLibrary.downloadImages.fulfilled]: downloadImagesFulfilled,
  [actions.mediaLibrary.downloadImages.rejected]: downloadImagesRejected,
  [actions.mediaLibrary.getFolders]: getFolders,
  [actions.mediaLibrary.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.mediaLibrary.getFolders.rejected]: getFoldersRejected,
  [actions.mediaLibrary.getImages]: getImages,
  [actions.mediaLibrary.getImages.fulfilled]: getImagesFulfilled,
  [actions.mediaLibrary.getImages.rejected]: getImagesRejected,
  [actions.mediaLibrary.moveImages]: moveImages,
  [actions.mediaLibrary.moveImages.fulfilled]: moveImagesFulfilled,
  [actions.mediaLibrary.moveImages.rejected]: moveImagesRejected,
  [actions.mediaLibrary.renameFolder]: renameFolder,
  [actions.mediaLibrary.renameFolder.fulfilled]: renameFolderFulfilled,
  [actions.mediaLibrary.renameFolder.rejected]: renameFolderRejected,
  [actions.mediaLibrary.saveImage]: saveImage,
  [actions.mediaLibrary.saveImage.fulfilled]: saveImageFulfilled,
  [actions.mediaLibrary.saveImage.rejected]: saveImageRejected,
  [actions.mediaLibrary.setValue]: setValue,
  [actions.mediaLibrary.toggleImageState]: toggleImageState,
  [actions.mediaLibrary.updateImage]: updateImage,
  [actions.mediaLibrary.updateImage.fulfilled]: updateImageFulfilled,
  [actions.mediaLibrary.updateImage.rejected]: updateImageRejected,
  [actions.mediaLibrary.uploadFiles]: uploadFiles,
  [actions.mediaLibrary.uploadFiles.fulfilled]: uploadFilesFulfilled,
  [actions.mediaLibrary.uploadFiles.rejected]: uploadFilesRejected,
};

export default mediaLibrary;
